import { useState, useEffect } from "react";
import { HashLoader } from "react-spinners";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import TableHead from "@mui/material/TableHead";
import Pagination from "@mui/material/Pagination";

import { getSalesOverview, getTickets } from "../../api/tickets";
import { MembershipDetailsDrawer } from "./MembershipDetailsDrawer";
import { ICONS } from "../../utils/icons";
import { COLORS } from "../../utils/colors";
import Status from "../../components/TicketStatus";
import timeAgo from "../../utils/timeAgo";
import { MainButton } from "../../components/MainButton";
import { CreateSoldMembershipDialog } from "./CreateSoldMembershipDialog";

const columns = [
  { label: "ID", id: "id", align: "center", minWidth: 50 },
  { label: "Image", id: "image", align: "center", minWidth: 50 },
  { label: "Customer", id: "name", align: "center", minWidth: 150 },
  { label: "Category", id: "category", align: "center", minWidth: 150 },
  { label: "Status", id: "status", align: "left" },
  { label: "Type", id: "type", align: "center", minWidth: 150 },
  { label: "Bought", id: "date", align: "center", minWidth: 150 },
  {
    label: "Listing Price",
    id: "amount",
    minWidth: 100,
    align: "center",
  },
];

const TICKETS_PER_PAGE = 50;

function createArrayOfObjects(originalArray, newDayPeriod) {
  const result = [];
  const currentDate = new Date();

  if (newDayPeriod == 0) {
    return originalArray;
  } else {
    for (let i = newDayPeriod - 1; i >= 0; i--) {
      const date = new Date(currentDate);
      date.setDate(date.getDate() - i);
      const formattedDate = date.toISOString().split("T")[0];

      const originalObject = originalArray.find(
        (obj) => obj._id === formattedDate
      );
      const tickets_sold = originalObject ? originalObject.tickets_sold : null;

      result.push({ _id: formattedDate, tickets_sold });
    }

    return result;
  }
}

export const SoldMemberships = ({ userData, handleSnackbar, dayPeriod }) => {
  const [loading, setLoading] = useState(false);
  const [numberOfMemberships, setNumberOfMemberships] = useState(0);
  const [ticketSold, setTicketSold] = useState([]);
  const [openCreatePopup, setOpenCreatePopup] = useState(false);
  const [ticketsArray, setTicketsArray] = useState([]);
  const [openTicketDialog, setTicketDialog] = useState(false);
  const [ticket, setTicket] = useState({});
  const [page, setPage] = useState(1);

  useEffect(async () => {
    setLoading(true);

    try {
      if (userData) {
        fetchData();
      }
    } catch (error) {
      handleSnackbar(error.message, "error");
    }
    setLoading(false);
  }, [page, dayPeriod]);

  const fetchData = async () => {
    const { data, error } = await getSalesOverview({
      dayPeriod,
      apiKey: userData.operators[0].api_key,
      type: "membership",
    });

    if (error) {
      if (!error.message.includes("No ticket found")) {
        handleSnackbar(error.message, "error");
      }
    }

    if (data) {
      data.sort((a, b) => a._id.localeCompare(b._id));
      setNumberOfMemberships(data.reduce((a, b) => a + b.tickets_sold, 0));

      const fullRangeData = createArrayOfObjects(data, dayPeriod);
      setTicketSold(fullRangeData);
    }

    const latestTicketSales = await getTickets({
      apiKey: userData.operators[0].api_key,
      page,
      dayPeriod,
      limit: TICKETS_PER_PAGE,
      types: "membership",
    });

    if (latestTicketSales.error) {
      if (!latestTicketSales.error.message.includes("No ticket found")) {
        handleSnackbar(latestTicketSales.error.message, "error");
      }
    }

    if (latestTicketSales.data) {
      setTicketsArray(latestTicketSales.data);
    }
  };

  const handleClick = (ticket) => {
    setTicket(ticket);
    setTicketDialog(true);
  };

  const handleOpenCreatePopup = () => {
    setOpenCreatePopup(true);
  };

  return (
    <div className="all-tickets-container">
      {openTicketDialog && (
        <MembershipDetailsDrawer
          open={openTicketDialog}
          setOpen={setTicketDialog}
          ticket={ticket}
          userData={userData}
          handleSnackbar={handleSnackbar}
        />
      )}
      {openCreatePopup && (
        <CreateSoldMembershipDialog
          openPopup={openCreatePopup}
          setOpenPopup={setOpenCreatePopup}
          userData={userData}
          handleSnackbar={handleSnackbar}
          apiKey={userData?.operators[0]?.api_key}
          fetchData={fetchData}
        />
      )}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(1, 1fr)",
          gap: "15px",
          margin: "25px 0px",
        }}
      >
        <div
          className="standard-container"
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: "20px",
          }}
        >
          <div>
            <h2>Sold Memberships</h2>
            <p id="subTitle" style={{ marginBottom: "20px" }}>
              Total number of memberships sold.
            </p>
            <MainButton
              text={"Create membership"}
              color={COLORS.black}
              hoverColor={COLORS.black}
              textColor={COLORS.white}
              borderColor={COLORS.black}
              shadowColor={COLORS.mainAccent}
              textHoverColor={COLORS.lightGrey}
              onClickAction={() => handleOpenCreatePopup()}
              width={"fit-content"}
            >
              {ICONS.membershipWhite20}
            </MainButton>
          </div>
          <div className="small-container">
            <div
              className="container-row"
              style={{
                width: "-webkit-fill-container",
                justifyContent: "space-between",
              }}
            >
              <p id="subheader">Total memberships sold</p>
              {ICONS.ticketAccent20}
            </div>
            <h2 style={{ fontSize: "34px", margin: "10px 0 0 0" }}>
              {loading ? <HashLoader size={20} /> : numberOfMemberships}
            </h2>
          </div>
        </div>
      </div>
      <p id="subheader" style={{ padding: "20px 0" }}>
        Membership Sales (showing 50 per page)
      </p>
      <Paper
        sx={{
          height: "fit-content",
          border: "1px solid #d8d8d8",
          boxShadow: "0",
        }}
      >
        <TableContainer>
          <Table stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    style={{
                      top: 0,
                      minWidth: column.minWidth,
                      fontFamily: "AuthenticSans90",
                      color: COLORS.grey,
                    }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {ticketsArray.length === 0 && (
                <p style={{ padding: "20px" }}>No membership sales yet</p>
              )}
              {ticketsArray.map((ticket, index) => {
                return (
                  <TableRow
                    hover
                    role="checkbox"
                    tabIndex={-1}
                    key={index}
                    onClick={() => handleClick(ticket)}
                  >
                    {columns.map((column) => {
                      return (
                        <TableCell
                          key={column.id}
                          align={column.align}
                          sx={{
                            padding: "10px",
                            fontFamily: "AuthenticSans90",
                            fontWeight: "bold",
                            cursor: "pointer",
                          }}
                        >
                          {column.id === "image" && (
                            <img
                              src={ticket.image || userData.logo}
                              style={{
                                width: "30px",
                                height: "30px",
                                objectFit: "cover",
                              }}
                            />
                          )}
                          {column.id === "id" && ticket._id.slice(21, 24)}
                          {column.id === "name" && ticket.customer.email}
                          {column.id === "status" && (
                            <Status status={ticket.status} />
                          )}
                          {column.id === "category" && ticket.category_name}
                          {column.id === "type" && ticket.type}
                          {column.id === "date" && timeAgo(ticket.createdAt)}
                          {column.id === "amount" &&
                            `${ticket.original_price.amount} ${ticket.original_price.currency}`}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "20px",
            width: "100%",
          }}
        >
          <Pagination
            count={Math.round(numberOfMemberships / TICKETS_PER_PAGE)}
            page={page}
            onChange={(event, value) => setPage(value)}
          />
        </div>
      </Paper>
      <div style={{ height: "50px" }} />
    </div>
  );
};
